import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useDialogs } from "../../../../dialog/use-dialog";
import { useNotification } from "../../../../../context/notification-provider";
import { DistinctValue } from "../../../types";
import { purgeValues } from "../../../services/cleaning-data-service";

interface PurgeValuesFooterProps {
  fieldId: number | undefined;
  checked: { [key: string]: boolean };
  mainValues: DistinctValue[];
  selectedItem: DistinctValue | null;
  refreshDistinctValues: () => void;
}

/**
 * Footer for Purge Values category (categoryId: 3)
 */
const PurgeValuesFooter: React.FC<PurgeValuesFooterProps> = ({
  fieldId,
  checked,
  mainValues,
  selectedItem,
  refreshDistinctValues,
}) => {
  const { confirm } = useDialogs();
  const { showNotification } = useNotification();

  const handlePurge = async () => {
    if (fieldId === undefined) {
      showNotification({
        message: "No field selected.",
        severity: "error",
        duration: 3000,
      });
      return;
    }

    // Get values to purge: prioritize checked items, then fall back to selected item, then all values
    let valuesToPurge: string[] = [];
    const checkedValues = mainValues
      .filter((val) => checked[val.id])
      .map((val) => val.value);

    if (checkedValues.length > 0) {
      valuesToPurge = checkedValues;
    } else if (selectedItem) {
      valuesToPurge = [selectedItem.value];
    } else {
      // If nothing selected, purge all values in the list
      valuesToPurge = mainValues.map((val) => val.value);
    }

    if (valuesToPurge.length === 0) {
      showNotification({
        message: "No values available to purge.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      const result = await confirm(
        `Are you sure you want to permanently purge ${valuesToPurge.length} value${valuesToPurge.length > 1 ? "s" : ""}? This action cannot be undone.`,
        {
          title: "Confirm Purge Values",
          okText: "Purge",
          cancelText: "Cancel",
          severity: "error",
        },
      );

      if (result === true) {
        try {
          await purgeValues(fieldId, valuesToPurge);
          refreshDistinctValues();
          showNotification({
            message: "Values purged successfully.",
            severity: "success",
            duration: 3000,
          });
        } catch (err) {
          showNotification({
            message: `Failed to purge values. Reason: ${(err as Error).message}`,
            severity: "error",
            duration: 3000,
          });
        }
      }
    } catch {
      showNotification({
        message: "Failed to show confirmation dialog.",
        severity: "error",
        duration: 3000,
      });
    }
  };

  const handleCancel = () => {
    // No action needed for cancel in purge
  };

  // Determine the message to display
  const checkedCount = Object.values(checked).filter(Boolean).length;
  const totalCount = mainValues.length;
  let message = `Purge all ${totalCount} value${totalCount === 1 ? "" : "s"} from the dataset.`;
  if (checkedCount > 0) {
    message = `Purge ${checkedCount} selected value${checkedCount === 1 ? "" : "s"} from the dataset.`;
  } else if (selectedItem) {
    message = `Purge "${selectedItem.value}" from the dataset.`;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid #eee",
          p: 2,
        }}
      >
        <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
          Warning: Selected values will be permanently removed from the dataset.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {message}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          borderTop: "1px solid #eee",
          p: 2,
        }}
      >
        <Button
          variant="contained"
          color="error"
          size="small"
          sx={{ mr: 1, borderRadius: 1 }}
          onClick={handlePurge}
        >
          Purge
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={{ borderRadius: 1 }}
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default PurgeValuesFooter;
